import type { Experiment, FeatureApiResponse, GrowthBook, Result } from '@growthbook/growthbook';
import { useEffect, useState } from 'react';

import { EProjectSection, trackEventV2 } from '@/services/analytics/trackEventV2';
import { getODAnonymousId } from '@/services/segment';
import { getCookie, removeCookie, setCookie } from '@/utility/cookie';
import { isSSR } from '@/utility/isSSR';

// Feature payloads are cached at the CDN and in the SDK, so this only bounds a cold first fetch.
const INIT_TIMEOUT_MS = 2000;

let growthbook: GrowthBook | null = null;
let setupPromise: Promise<void> | null = null;
let currentAnonId: string | null = null;

const exposedThisLoad = new Set<string>();

const OVERRIDE_COOKIE = 'od-exp-gb';
const OVERRIDE_PARAM = /^od-exp\[(.+)\]$/;

// Through Segment, so GrowthBook's Redshift queries join exposure to conversions on one identity; messageId lets Segment drop repeats.
const trackExposure = (experiment: Experiment<unknown>, result: Result<unknown>) => {
  if (!currentAnonId) return;
  const key = `${currentAnonId}:${experiment.key}:${result.key}`;
  if (exposedThisLoad.has(key)) return;
  exposedThisLoad.add(key);
  return trackEventV2('Experiment Viewed', EProjectSection.MARKETPLACE, {
    experimentKey: experiment.key,
    variationKey: result.key,
    experimentSource: 'growthbook',
    messageId: `growthbook:${key}`,
  });
};

const setup = async (payload?: FeatureApiResponse | null) => {
  const apiHost = process.env.growthbookApiHost;
  const clientKey = process.env.growthbookClientKey;
  if (!apiHost || !clientKey) return;
  try {
    currentAnonId = await getODAnonymousId();
    const { GrowthBook: GrowthBookSdk } = await import('@growthbook/growthbook');
    const instance = new GrowthBookSdk({
      apiHost,
      clientKey,
      attributes: { id: currentAnonId },
      trackingCallback: trackExposure,
      // Flags only: never run visual-editor scripts or redirects served by the GrowthBook server.
      disableVisualExperiments: true,
      disableUrlRedirectExperiments: true,
      backgroundSync: false,
    });
    if (payload) {
      instance.initSync({ payload });
    } else {
      await instance.init({ timeout: INIT_TIMEOUT_MS });
    }
    growthbook = instance;
  } catch {
    growthbook = null;
  }
};

const ensureSetup = (payload?: FeatureApiResponse | null) => {
  if (isSSR()) return Promise.resolve();
  setupPromise ??= setup(payload);
  return setupPromise;
};

// Logout resets the anonymous id for visitors on the legacy Segment cookie; re-bucket on the next read.
const syncAnonId = async (instance: GrowthBook) => {
  const anonId = await getODAnonymousId();
  if (anonId === currentAnonId) return;
  currentAnonId = anonId;
  await instance.setAttributes({ id: anonId });
};

const readOverrides = (): Record<string, unknown> => {
  try {
    const stored: unknown = JSON.parse(getCookie(OVERRIDE_COOKIE) ?? '{}');
    return stored && typeof stored === 'object' ? { ...stored } : {};
  } catch {
    return {};
  }
};

// QA forcing via ?od-exp[feature]=true|false, kept for the browser session; any other value clears it.
const getOverride = (feature: string): boolean | undefined => {
  const overrides = readOverrides();
  let changed = false;
  new URLSearchParams(window.location.search).forEach((value, key) => {
    const flag = key.match(OVERRIDE_PARAM)?.[1];
    if (!flag) return;
    changed = true;
    if (value === 'true' || value === 'false') {
      overrides[flag] = value === 'true';
    } else {
      delete overrides[flag];
    }
  });
  if (changed) {
    if (Object.keys(overrides).length) {
      setCookie(OVERRIDE_COOKIE, JSON.stringify(overrides));
    } else {
      removeCookie(OVERRIDE_COOKIE);
    }
  }
  const override = overrides[feature];
  return typeof override === 'boolean' ? override : undefined;
};

const isFeatureOn = async (
  feature: string,
  payload?: FeatureApiResponse | null,
): Promise<boolean> => {
  const override = getOverride(feature);
  if (override !== undefined) return override;
  await ensureSetup(payload);
  if (!growthbook) return false;
  try {
    await syncAnonId(growthbook);
    return growthbook.isOn(feature);
  } catch {
    return false;
  }
};

/** undefined until resolved (always on the server); false when unconfigured, unreachable or !shouldRun. Pass getGrowthBookPayload() from getStaticProps to skip the features fetch. */
export const useGrowthBookFeature = (
  feature: string,
  { shouldRun = true, payload }: { shouldRun?: boolean; payload?: FeatureApiResponse | null } = {},
): boolean | undefined => {
  const [enabled, setEnabled] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    if (!shouldRun) return;
    let active = true;
    isFeatureOn(feature, payload).then(on => {
      if (active) setEnabled(on);
    });
    return () => {
      active = false;
    };
  }, [feature, shouldRun, payload]);

  return shouldRun ? enabled : false;
};
